import * as React from 'react';
import { forwardRef, useContext, useEffect, useState } from 'react';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import StudentUtil from '../../utils/StudentUtil';
import { VariableSizeList } from 'react-window';
import { colors, Stack } from '@mui/material';
import { Field } from 'formik';

const LISTBOX_PADDING = 8; // px

const ControlContext = React.createContext({});
const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

const Row = ({ data, index, style }) => {
  const { selectedValues, setSelectedValues, form, name } = useContext(ControlContext);
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: style.top + LISTBOX_PADDING,
  };

  const getGroupByState = dataSet => {
    let allSelected = true;
    let anySelected = false;
    dataSet.children.forEach(child => {
      if (!child.selected) allSelected = false;
      else anySelected = true;
    });
    if (allSelected) return 'allSelected';
    else if (anySelected) return 'indeterminate';
    return null;
  };

  const onGroupClick = (dataSet, checked) => {
    if (!checked) {
      dataSet.children.forEach(child => selectedValues.push(child.option));
      const newSelectedValues = [...new Set(selectedValues)];
      setSelectedValues(newSelectedValues);
      form.setFieldValue(name, newSelectedValues.map(v => v.id));
    } else {
      const options = dataSet.children.map(c => c.option);
      const newSelectedValues = selectedValues.filter(v => !options.includes(v));
      setSelectedValues(newSelectedValues);
      form.setFieldValue(name, newSelectedValues.map(v => v.id));
    }
  };

  if (dataSet.hasOwnProperty('group')) {
    const state = getGroupByState(dataSet);
    return (
      <li
        key={dataSet.key}
        style={{ ...inlineStyle, backgroundColor: colors.grey[200], cursor: 'pointer' }}
        onClick={() => {
          onGroupClick(dataSet, state === 'allSelected');
        }}
      >
        <Stack direction="row" alignItems="center" height="100%">
          <Checkbox
            style={{ marginRight: 8 }}
            checked={state === 'allSelected'}
            indeterminate={state === 'indeterminate'}
          />
          {dataSet.group}
        </Stack>
      </li>
    );
  }

  return (
    <li {...dataSet.props} style={inlineStyle}>
      <Checkbox style={{ marginRight: 8 }} checked={dataSet.selected} />
      {dataSet.label}
    </li>
  );
};

const ListBox = forwardRef(({ children, ...other }, ref) => {
  const itemData = [];
  children.forEach(item => {
    itemData.push(item);
    if (item.children && item.children.length > 0) {
      itemData.push(...item.children);
    }
  });

  const itemCount = itemData.length;
  const itemSize = 48;

  const getChildSize = child => {
    if (child.hasOwnProperty('group')) {
      return 48;
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={index => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {Row}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});
const StudentAutocomplete = ({ name, initialValues }) => {
  const [options, setOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedValues, setSelectedValues] = useState([]);

  useEffect(() => {
    StudentUtil.loadAutoCompleteOptions().then(response => {
      setOptions(response.data);
      if (initialValues) {
        setSelectedValues(response.data.filter(option => initialValues.includes(option.id)));
      }
    });
  }, [initialValues]);

  return (
    <Field name={name}>
      {({ field, form, ...props }) => (
        <ControlContext.Provider value={{ options, selectedValues, setSelectedValues, form, name }}>
          <Autocomplete
            multiple
            options={options}
            disableCloseOnSelect
            getOptionLabel={option => option.fullName + option.id + option.class}
            groupBy={option => option.class}
            ListboxComponent={ListBox}
            limitTags={0}
            value={selectedValues}
            onChange={(event, value) => {
              setSelectedValues(value);
              form.setFieldValue(
                name,
                value.map(v => v.id),
              );
            }}
            onClose={() => setSearchTerm('')}
            inputValue={searchTerm}
            renderTags={() => null}
            renderOption={(props, option, { selected, index }) => ({
              option,
              label: option.fullName,
              selected,
              index,
              props,
            })}
            renderGroup={params => params}
            renderInput={params => (
              <TextField
                {...params}
                variant="standard"
                label="Gekoppelde leerlingen"
                InputLabelProps={{ shrink: true }}
                onChange={event => {
                  setSearchTerm(event.target.value);
                }}
                placeholder={`${selectedValues.length} leerling(en) geselecteerd`}
              />
            )}
          />
        </ControlContext.Provider>
      )}
    </Field>
  );
};

export default StudentAutocomplete;
